import { useReadOnly } from 'utils/lifecycle'
import { browserDate } from 'utils/time'
import React, { useState } from 'react'
import { useSnackbar } from 'notistack'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Add, Delete, Pause, PlayArrow } from '@mui/icons-material'
import useSWR from 'swr'
import AccountPage from '../../components/AccountPage'
import api from 'utils/api'
import { Loader } from 'components'
import CreateAlertDialog from './CreateAlertDialog'
import AccountPageIntro from '../../components/AccountPageIntro'

const alertTarget = (alert) => {
  if (alert.type === 'BUSINESS') return alert.business || `Business ${alert.businessId}`
  if (alert.type === 'CATEGORY') return alert.category || `Category ${alert.categoryId}`
  if (alert.type === 'LEGACY') return alert.query || alert.business || alert.category || 'Saved search'
  return alert.keyword || alert.query || '-'
}

const alertTypeLabel = (type) => ({
  KEYWORD: 'Keyword',
  BUSINESS: 'Business',
  CATEGORY: 'Category',
  LEGACY: 'Saved search',
}[type] || 'Alert')

const formatCreatedAt = (value) => {
  if (!value) return '-'
  const date = browserDate(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString()
}

const Alerts = () => {
  const readOnly = useReadOnly()
  const { enqueueSnackbar } = useSnackbar()
  const [createOpen, setCreateOpen] = useState(false)
  const [workingId, setWorkingId] = useState(null)
  const url = '/user/query/list?size=100&sort=sysCreated,desc'
  const { data, error, mutate } = useSWR(url, () => api.get(url), {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  })

  const updateActive = (alert) => async () => {
    setWorkingId(alert.id)
    try {
      await api.patch(`/user/query/${alert.id}/active`, { active: !alert.active })
      await mutate()
      enqueueSnackbar(alert.active ? 'Alert paused' : 'Alert resumed', { variant: 'success' })
    } catch (requestError) {
      enqueueSnackbar('Cannot update your alert', { variant: 'error' })
    } finally {
      setWorkingId(null)
    }
  }

  const handleDelete = (id) => async () => {
    setWorkingId(id)
    try {
      await api.delete(`/user/query/delete/${id}`)
      await mutate()
      enqueueSnackbar('Alert deleted', { variant: 'success' })
    } catch (requestError) {
      enqueueSnackbar('Cannot delete your alert', { variant: 'error' })
    } finally {
      setWorkingId(null)
    }
  }

  if (error) {
    return <AccountPage title="Alerts"><Box p={2}>Could not load your alerts.</Box></AccountPage>
  }

  if (!data) {
    return <AccountPage title="Alerts"><Box p={2}><Loader /></Box></AccountPage>
  }

  const alerts = data.content || []

  return (
    <AccountPage title="Alerts">
      <Box p={2} pt={0}>
        <AccountPageIntro
          action={(
            <Button disabled={readOnly}
              size="small"
              startIcon={<Add fontSize="small" />}
              sx={{ fontSize: 14 }}
              variant="contained"
              onClick={() => setCreateOpen(true)}
            >
              Create alert
            </Button>
          )}
        >
          We send at most one daily email when new offers match your active alerts. Unsubscribing through an email stops all offer-alert emails. Pausing or deleting an alert here only changes that saved alert. To receive emails again after unsubscribing, use the email preferences link in a previous alert email.
        </AccountPageIntro>

        {alerts.length === 0 ? (
          <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center' }}>
            <Typography sx={{ mb: 2 }}>You don't have any alerts yet.</Typography>
            <Button disabled={readOnly} variant="contained" onClick={() => setCreateOpen(true)}>Create your first alert</Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Following</TableCell>
                  <TableCell>Area</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow key={alert.id}>
                    <TableCell>{alertTypeLabel(alert.type)}</TableCell>
                    <TableCell>{alertTarget(alert)}</TableCell>
                    <TableCell>{alert.location || alert.filter || 'Saved location'}</TableCell>
                    <TableCell>{formatCreatedAt(alert.createdAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" color={alert.active ? 'success' : 'default'} label={alert.active ? 'Active' : 'Paused'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={alert.active ? 'Pause alert' : 'Resume alert'}>
                        <span>
                          <IconButton
                            aria-label={alert.active ? 'Pause alert' : 'Resume alert'}
                            disabled={readOnly || workingId === alert.id}
                            onClick={updateActive(alert)}
                          >
                            {alert.active ? <Pause /> : <PlayArrow />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete alert">
                        <span>
                          <IconButton
                            aria-label="Delete alert"
                            disabled={readOnly || workingId === alert.id}
                            onClick={handleDelete(alert.id)}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      <CreateAlertDialog
        open={createOpen && !readOnly}
        onClose={() => setCreateOpen(false)}
        onCreated={() => mutate()}
      />
    </AccountPage>
  )
}

export default Alerts
